import { z } from "zod";

// ============================================
// Click Tracking Types for Job Application Flow
// ============================================

/**
 * UTM Parameters for marketing attribution
 */
export const UtmParamsSchema = z.object({
    utm_source: z.string().default(""),
    utm_medium: z.string().default(""),
    utm_campaign: z.string().default(""),
    utm_term: z.string().default(""),
    utm_content: z.string().default(""),
});

export type UtmParamsDto = z.infer<typeof UtmParamsSchema>;

/**
 * Device information collected from client
 */
export const DeviceInfoSchema = z.object({
    type: z.enum(["mobile", "tablet", "desktop"]).default("desktop"),
    vendor: z.string().optional(),
    model: z.string().optional(),
    os: z.string().optional(),
    userAgent: z.string().optional(),
    screenResolution: z.string().default("unknown"),
});

export type DeviceInfoDto = z.infer<typeof DeviceInfoSchema>;

/**
 * Browser information
 */
export const BrowserInfoSchema = z.object({
    name: z.string().optional(),
    version: z.string().optional(),
    engine: z.string().optional(),
});

export type BrowserInfoDto = z.infer<typeof BrowserInfoSchema>;

/**
 * Session tracking information
 */
export const SessionInfoSchema = z.object({
    id: z.string().default("unknown"),
    referrer: z.string().default("unknown"),
    utmParams: UtmParamsSchema.optional(),
});

export type SessionInfoDto = z.infer<typeof SessionInfoSchema>;

/**
 * User information (optional, for authenticated users)
 */
export const UserInfoSchema = z.object({
    id: z.string().default("undefined"),
    email: z.string().default("undefined"),
});

export type UserInfoDto = z.infer<typeof UserInfoSchema>;

/**
 * Complete telemetry payload sent from client
 */
export const ClientTelemetrySchema = z.object({
    device: DeviceInfoSchema.optional(),
    browser: BrowserInfoSchema.optional(),
    session: SessionInfoSchema.optional(),
    user: UserInfoSchema.optional(),
    browserLanguage: z.string().optional(),
});

export type ClientTelemetryDto = z.infer<typeof ClientTelemetrySchema>;

/**
 * Request body for POST /api/jobs/apply-click
 * Either job_id or ref_id must be provided
 */
export const ApplyClickRequestSchema = z
    .object({
        // Job identifier (at least one required)
        job_id: z.string().optional(),
        ref_id: z.string().optional(),

        // Optional override for redirect URL
        redirect_url: z.string().url().optional(),

        // Portal context
        portal_id: z.string().optional(),

        // Domain/client info (optional, can be derived from portal)
        domain_name: z.string().optional(),
        client_name: z.string().optional(),

        // Client-collected telemetry
        telemetry: ClientTelemetrySchema.optional(),
    })
    .refine((data) => data.job_id || data.ref_id, {
        message: "Either job_id or ref_id must be provided",
    });

export type ApplyClickRequestDto = z.infer<typeof ApplyClickRequestSchema>;

/**
 * Response from POST /api/jobs/apply-click
 */
export const ApplyClickResponseSchema = z.object({
    redirect_url: z.string(),
    ac_id: z.string(),
});

export type ApplyClickResponseDto = z.infer<typeof ApplyClickResponseSchema>;

/**
 * Metadata object embedded in Kafka event (JSON stringified)
 */
export interface RedirectEventMetadata {
    device: {
        type: string;
        vendor: string;
        model: string;
        os: string;
        userAgent: string;
        screenResolution: string;
    };
    browser: {
        name: string;
        version: string;
        engine: string;
    };
    session: {
        id: string;
        referrer: string;
        utmParams: UtmParamsDto;
    };
    network: {
        ipAddress: string;
        browserLanguage: string;
    };
    click: {
        id: string;
        timestamp: string;
    };
    user: {
        id: string;
        email: string;
    };
}

/**
 * Complete Kafka event payload for 'redirected' topic
 */
export interface RedirectEventPayload {
    // Source identifier
    source: string;

    // Core identifiers
    event_id: string;
    job_id: string;
    user_id: string;
    user_email: string;

    // Location
    country: string;

    // Event info
    event_type: "redirect";
    recorded_at: string;
    metadata: string; // JSON stringified RedirectEventMetadata

    // Device/Browser info (denormalized for querying)
    ip_address: string;
    user_agent: string;
    device_type: string;
    operating_system: string;
    referrer: string;
    utm_params: UtmParamsDto;

    // Cost model
    cost_model_cpa: boolean;
    cost_model_cpc: boolean;
    cost_model_rph: boolean;
    cpa_value: number;
    cpc_value: number;
    rph_value: number;

    // Status
    status: string;

    // Portal/Client info
    domain_name?: string;
    client_name?: string;
    portal_id?: string;

    // Job info
    job_title: string;
    aggregator_id?: string;
    aggregator_name?: string;
    isAggregatorJob: boolean;
    aggRefId?: string;
    feed_id?: string;
    job_country?: string;
}

/**
 * Job details response from job-index API
 * Subset of fields needed for click tracking
 */
export interface JobIndexJobDetails {
    id: string;
    title: string;
    url: string;
    ref_id: string;
    company?: string;
    country?: string;
    aggregator_id?: string;
    aggregator_name?: string;
    feed_id?: string;
    cpa_value?: number;
    cpc_value?: number;
    rph_value?: number;
}
